import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  ExternalLink,
  Globe,
  Loader2,
  RefreshCcw,
  SkipForward,
  X,
} from "lucide-react";
import type {
  CfoAction,
  CfoActionAnswerRead,
  CfoActionList,
  CfoFounderAddition,
  CfoResearchFounder,
  CfoWorkItem,
} from "@/api/types";
import { addFounder, reviewAffiliation } from "@/api/cfoAffiliations";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CfoActionsPanel, type CfoActionRequest } from "@/components/companies/CfoActionsPanel";
import { CFO_DECISIONS, inputLabel } from "@/lib/cfoLabels";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { CfoResearchState } from "@/hooks/useCfoResearch";
import {
  foundersNeed,
  FUNDING_DIMENSIONS,
  JOB_DIMENSIONS,
  TEAM_DIMENSIONS,
  companyLinks,
  currentMissing,
  needOf,
} from "./cfoResearch";
import {
  FoundersSection,
  FundingSection,
  HiringSection,
  HistorySection,
  LinkRow,
  TeamSection,
} from "./CfoResearchSections";

const FLAGS = {
  review: { label: "Needs review", className: "bg-sky-50 text-sky-800 ring-sky-200" },
  data: { label: "Missing data", className: "bg-amber-50 text-amber-800 ring-amber-200" },
} as const;

/** One company of a CFO research lot: what its decision waits for, answered in
 *  place, and everything the sources already say to find it. Closing is the
 *  container's: Done once something was answered, Can't find with a note, or
 *  Skip to hand it back untouched. */
export function CfoResearchCard({
  slug,
  item,
  research,
  canAnswer,
  answers,
  busy,
  cantFindOpen,
  onCantFindOpen,
  onAnswered,
  onFounderConfirmed,
  onRescored,
  onDone,
  onSkip,
  onCantFind,
}: {
  slug: string;
  item: CfoWorkItem;
  research: CfoResearchState;
  canAnswer: boolean;
  /** Answers recorded on this company in this session. */
  answers: number;
  busy: boolean;
  cantFindOpen: boolean;
  onCantFindOpen: (open: boolean) => void;
  onAnswered: (result: CfoActionAnswerRead, action: CfoAction) => void;
  /** A founder of the company confirmed from the sheet: it counts as work done. */
  onFounderConfirmed: (founderId: string) => void;
  onRescored: () => void;
  onDone: () => void;
  onSkip: () => void;
  onCantFind: (note: string) => void;
}) {
  const brief = research.data?.brief ?? null;
  const explanation = research.data?.explanation ?? null;
  const [actions, setActions] = useState<CfoActionList | null>(null);
  const [request, setRequest] = useState<CfoActionRequest | null>(null);
  // Remounts the actions panel after a founder review: it then follows the re-scoring.
  const [panel, setPanel] = useState(0);
  const [note, setNote] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cantFindOpen) noteRef.current?.focus();
  }, [cantFindOpen]);

  // Follows the re-scorings of this card; the lot's list until the first read.
  const missing = currentMissing(actions) ?? item.missing;
  // Re-scored to a decision while on this card.
  const decidedNow =
    actions?.state === "CURRENT" &&
    actions.decision &&
    !["NEEDS_DATA", "NEEDS_REVIEW"].includes(actions.decision)
      ? CFO_DECISIONS[actions.decision] ?? null
      : null;
  // For a company to review, the founder place is what decides: said once, above.
  const listed = item.flag === "review" ? missing.filter((m) => m !== "founder_geography") : missing;
  const placeAction = (actions?.actions ?? []).find((a) => a.kind === "FOUNDER_PLACE");
  const flag = FLAGS[item.flag];
  const decision = item.decision ? CFO_DECISIONS[item.decision] : null;
  const has = (dimensions: string[]) => dimensions.some((d) => missing.includes(d));
  // What the Founders block waits for, precisely: a founder to find or confirm, a
  // career to read, the items that keep a US-only career open, or a place to review.
  const founders = foundersNeed(
    brief,
    missing,
    item.flag === "review" && (actions?.state !== "CURRENT" || actions.decision === "NEEDS_REVIEW"),
    (actions?.actions ?? []).some((a) => a.kind === "SCHOOL_COUNTRY"),
  );
  const rescoring = actions?.state === "REEVALUATING";

  async function confirmFounder(
    founder: CfoResearchFounder,
    reason: string,
    evidenceIds: string[],
  ): Promise<boolean> {
    try {
      await reviewAffiliation(slug, item.id, founder.id, {
        expected_revision: founder.revision,
        status: "CONFIRMED",
        reason,
        evidence_ids: evidenceIds,
        suppress: false,
      });
      toast.success("Founder confirmed", {
        description: "Their documented places now count. The company is re-scored.",
      });
      research.reload();
      setPanel((n) => n + 1);
      onFounderConfirmed(founder.id);
      return true;
    } catch (error) {
      const detail = isAxiosError(error)
        ? (error.response?.data as { detail?: unknown } | undefined)?.detail
        : null;
      if (detail === "CFO_AFFILIATION_VERSION_CHANGED") {
        toast.error("This founder changed meanwhile. The sheet was refreshed.");
        research.reload();
      } else if (isAxiosError(error) && error.response?.status === 403) {
        toast.error("You are not allowed to confirm founders.");
      } else {
        toast.error("The founder was not confirmed. Try again.");
      }
      return false;
    }
  }

  async function addFounderFound(founder: CfoFounderAddition): Promise<string | null> {
    try {
      const added = await addFounder(slug, item.id, founder);
      toast.success("Founder added", {
        description: "Confirmed under your name. The company is re-scored; then document their place.",
      });
      research.reload();
      setPanel((n) => n + 1);
      onFounderConfirmed(added.id);
      return added.id;
    } catch (error) {
      const detail = isAxiosError(error)
        ? (error.response?.data as { detail?: unknown } | undefined)?.detail
        : null;
      if (detail === "CFO_FOUNDER_ALREADY_LISTED") {
        toast.error("This person is already listed for the company: confirm them instead.");
      } else if (isAxiosError(error) && error.response?.status === 422) {
        toast.error("Check the LinkedIn profile and the source addresses.");
      } else if (isAxiosError(error) && error.response?.status === 403) {
        toast.error("You are not allowed to add founders.");
      } else {
        toast.error("The founder was not added. Try again.");
      }
      return null;
    }
  }

  function documentPlace(founder: CfoResearchFounder) {
    if (!placeAction) return;
    setRequest({
      key: Date.now(),
      actionId: placeAction.id,
      founderId: founder.id,
      sourceUrl: founder.linkedin ?? null,
    });
    actionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // The blocks the decision waits for come first.
  const sections = [
    {
      key: "founders",
      missing: Boolean(founders),
      node: (
        <FoundersSection
          key="founders"
          brief={brief}
          need={founders?.label}
          detail={founders?.detail}
          onDocument={placeAction && canAnswer ? documentPlace : undefined}
          onConfirm={canAnswer ? confirmFounder : undefined}
          onAdd={canAnswer ? addFounderFound : undefined}
        />
      ),
    },
    {
      key: "funding",
      missing: has(FUNDING_DIMENSIONS),
      node: (
        <FundingSection key="funding" brief={brief} need={needOf(FUNDING_DIMENSIONS, missing)} />
      ),
    },
    {
      key: "team",
      missing: has(TEAM_DIMENSIONS),
      node: (
        <TeamSection
          key="team"
          company={item.name}
          explanation={explanation}
          loaded={research.data !== null}
          missing={missing}
        />
      ),
    },
    {
      key: "jobs",
      missing: has(JOB_DIMENSIONS),
      node: (
        <HiringSection
          key="jobs"
          company={item.name}
          domain={item.domain}
          explanation={explanation}
          loaded={research.data !== null}
          missing={missing}
        />
      ),
    },
  ].sort((a, b) => Number(b.missing) - Number(a.missing));

  return (
    <div className="space-y-4">
      <header className="rounded-plane bg-white p-6 shadow-plane" data-company-id={item.id}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2
              id="cfo-research-company"
              className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900"
            >
              {item.name}
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                  flag.className,
                )}
              >
                {flag.label}
              </span>
              {decision && item.decision !== (item.flag === "review" ? "NEEDS_REVIEW" : "NEEDS_DATA") && (
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                    decision.className,
                  )}
                >
                  {decision.label}
                </span>
              )}
            </h2>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
              {item.domain && (
                <a
                  href={`https://${item.domain}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 hover:text-indigo-600"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {item.domain}
                </a>
              )}
              {typeof item.score === "number" && (
                <span className="tabular-nums">Score {item.score}/100</span>
              )}
              {item.evaluated_at && <span>Evaluated {formatDate(item.evaluated_at)}</span>}
              <a
                href={`/projects/${encodeURIComponent(slug)}/companies/${item.id}`}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
              >
                Company page
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
        </div>
        {brief?.description && (
          <p className="mt-3 line-clamp-3 text-sm text-slate-600">{brief.description}</p>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {item.flag === "review" ? (
            <>
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                Decided by
              </span>
              <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800 ring-1 ring-inset ring-sky-200">
                The country of a founder place
              </span>
              {listed.length > 0 && (
                <span className="ml-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Also missing
                </span>
              )}
            </>
          ) : (
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Waits for
            </span>
          )}
          {listed.map((key) => (
            <span
              key={key}
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                item.flag === "review"
                  ? "bg-slate-50 text-slate-600 ring-slate-200"
                  : "bg-amber-50 text-amber-800 ring-amber-200",
              )}
            >
              {inputLabel(key)}
            </span>
          ))}
          {!missing.length && item.flag === "data" && (
            <span className="text-xs text-slate-500">
              {decidedNow ? (
                <>
                  Nothing any more ·{" "}
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 font-medium ring-1 ring-inset",
                      decidedNow.className,
                    )}
                  >
                    {decidedNow.label}
                  </span>
                </>
              ) : (
                "—"
              )}
            </span>
          )}
        </div>
        <div className="mt-4 border-t border-slate-100 pt-4">
          <LinkRow
            links={companyLinks({
              name: item.name,
              domain: item.domain,
              website: brief?.website,
              crunchbase_url: brief?.crunchbase_url,
            })}
          />
        </div>
      </header>

      <div ref={actionsRef} className="scroll-mt-24">
        <CfoActionsPanel
          key={`${item.id}:${panel}`}
          slug={slug}
          companyId={item.id}
          canAnswer={canAnswer}
          request={request}
          onLoaded={setActions}
          onAnswered={onAnswered}
          onRescored={onRescored}
        />
      </div>

      {research.failed ? (
        <div className="flex items-center justify-between gap-4 rounded-plane bg-white px-5 py-3 text-sm text-slate-600 shadow-plane">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            The research sheet could not be loaded.
          </span>
          <Button variant="ghost" size="sm" onClick={research.reload}>
            <RefreshCcw />
            Retry
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {sections.map((section) => (
            <div key={section.key} className={cn(section.key === "founders" && "lg:col-span-2")}>
              {section.node}
            </div>
          ))}
          <div className="lg:col-span-2">
            <HistorySection brief={brief} />
          </div>
        </div>
      )}

      <footer className="sticky bottom-4 z-10 rounded-plane bg-white/95 p-4 shadow-plane ring-1 ring-slate-900/[0.06] backdrop-blur">
        {cantFindOpen ? (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              onCantFind(note);
            }}
          >
            <label htmlFor="cfo-cant-find" className="text-sm font-medium text-slate-900">
              Nothing found? Note what you checked
              <span className="font-normal text-slate-400"> (optional)</span>
            </label>
            <Textarea
              id="cfo-cant-find"
              ref={noteRef}
              rows={2}
              maxLength={2000}
              value={note}
              disabled={busy}
              placeholder="e.g. “No LinkedIn education for either founder; careers page lists no finance role”"
              onChange={(e) => setNote(e.target.value)}
            />
            <p className="text-xs text-slate-500">
              {answers
                ? "Your answers are kept and the note is added to them. Once re-scored, the company comes back if something is still missing."
                : "The company leaves the queue until new data changes its evaluation. The note stays on its timeline for the next person."}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onCantFindOpen(false)}
              >
                Back
              </Button>
              <Button
                type="submit"
                variant={answers ? "primary" : "destructive"}
                size="sm"
                disabled={busy}
              >
                {busy ? <Loader2 className="animate-spin" /> : answers ? <Check /> : <X />}
                {answers ? "Save note · next" : "Can’t find · next"}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSkip}
                disabled={busy}
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              >
                <SkipForward className="h-4 w-4" /> Skip
                <kbd className="ml-1 rounded bg-slate-100 px-1 text-[9px] text-slate-400">S</kbd>
              </button>
              <button
                type="button"
                onClick={() => onCantFindOpen(true)}
                disabled={busy}
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-rose-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
              >
                <X className="h-4 w-4" /> Can&rsquo;t find
                <kbd className="ml-1 rounded bg-rose-50 px-1 text-[9px] text-rose-400">X</kbd>
              </button>
            </div>
            <div className="flex items-center gap-3">
              <p className="hidden text-xs text-slate-500 sm:block">
                {answers
                  ? `${answers} answer${answers === 1 ? "" : "s"} saved${rescoring ? " · re-scoring" : ""}`
                  : "Answers and founder reviews are saved as you go"}
              </p>
              <Button
                size="lg"
                variant="primary"
                disabled={busy || answers === 0}
                title={answers === 0 ? "Answer at least one action first" : undefined}
                onClick={onDone}
              >
                {busy ? <Loader2 className="animate-spin" /> : <Check />}
                Done · next
              </Button>
            </div>
          </div>
        )}
      </footer>
    </div>
  );
}
